import React, { useEffect, useRef, useState } from "react";
import type { CalendarApi, EventApi } from "@fullcalendar/react";
import Box from "@mui/material/Box";
import FormGroup from "@mui/material/FormGroup";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { InferType } from "yup";

import SVG from "@/assets/svg";
import { Visit, VisitStatus } from "@/interfaces/IServiceUser";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  setSelectedVisit,
  toggleCarerModal,
  updateSingleVisit,
} from "@/redux/reducers/roster";
import { getServiceRoster } from "@/services/site";
import { getStaff } from "@/services/staff";
import logger from "@/runtime-utils/observability/logger";
import { allocateAnotherStaffSchema } from "@/utils/validators";
import CustomAvatar from "../avatar";
import CostumeButton from "../button";
import { AutoCompleteInput, SelectInput } from "../input";
import ListView from "../list";
import { errorToast, loadingToast, successToast } from "../toasts";
import { DrawerStyled } from "./drawerStyled";
import { Staff } from "@/interfaces/IAuth";

type SelectOptions = {
  value: string;
  label: string;
};

type AllocateStaff = InferType<typeof allocateAnotherStaffSchema>;
type Props = {
  getCalendarApi: () => CalendarApi | null;
  updateVisit: (
    id: string,
    visit: any,
    oldVisit: Partial<Visit>
  ) => Promise<void>;
  staffLogLoading: boolean;
  staffLogCount: Record<string, number>;
};
function CareTeam({
  staffLogCount,
  staffLogLoading,
  updateVisit,
  getCalendarApi,
}: Props) {
  const dispatch = useAppDispatch();
  const [show, setShow] = useState(false);
  const handleOpenCarerModal = () => dispatch(toggleCarerModal(true));
  const {
    selectedVisit,
    visits: { data: visits },
  } = useAppSelector((state) => state.roster);
  const [options, setOptions] = useState<SelectOptions[]>([]);

  const [carerAssigned, setCarerAssigned] = useState(
    selectedVisit?.extendedProps?.carerAssigned || []
  );

  const [anotherCarers, setAnotherCarers] = useState<
    { carer: Staff; type: string }[]
  >(selectedVisit?.extendedProps?.anotherCarers || []);

  const filteredOptions = options.filter(
    (option: any) =>
      !carerAssigned.some(
        (assignedCarer: any) => assignedCarer._id === option.value
      ) && !anotherCarers.some((c: any) => c.carer._id === option.value)
  );

  const { data: terms } = useAppSelector((state) => state.terms.terms);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedCarer, setSelectedCarer] = useState<string>("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [carerType, setCarerType] = useState<"carerAssigned" | "anotherCarers">(
    "carerAssigned"
  );

  // const [roster, setRoster] = useState("");
  const [roster, setRoster] = useState<SelectOptions[]>([]);

  const getAJobRoleRoutes = async () => {
    const response = await getServiceRoster();

    // logger.debug("my ethecity data", response);
    if (response.success) {
      const formattedData = response?.data?.map((el: any) => ({
        label: el?.name,
        value: el?.name,
      }));
      setRoster(formattedData);
    }
  };

  useEffect(() => {
    getAJobRoleRoutes();
  }, []);

  const [relatedResources, setRelatedResources] = useState<
    Visit["carerAssigned"]
  >(selectedVisit?.extendedProps?.carerAssigned || []);
  const visit = selectedVisit?.extendedProps as Visit;
  const {
    values,
    touched,
    errors,
    isSubmitting,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    setFieldTouched, // Add this to manually set touched state
  } = useFormik<AllocateStaff>({
    initialValues: {
      staff: {
        label: "",
        value: "",
      },
      type: "",
    },
    validationSchema: allocateAnotherStaffSchema,
    // onSubmit: async (values) => {
    //   const body = visit?.anotherCarers?.length
    //     ? {
    //         anotherCarers: [
    //           ...visit.anotherCarers?.map((c) => ({
    //             carer: c.carer._id,
    //             type: c.type,
    //           })),
    //           {
    //             carer: values.staff.value,
    //             type: values.type,
    //           },
    //         ],
    //       }
    //     : {
    //         anotherCarers: [
    //           {
    //             carer: values.staff.value,
    //             type: values.type,
    //           },
    //         ],
    //       };

    //   try {
    //     await handleUpdateVisit(body);
    //     setShow(false);
    //     dispatch(setSelectedVisit(null));
    //     successToast("Visit updated successfully");
    //   } catch (error) {
    //     errorToast("Failed to update visit", {
    //       autoClose: 3500,
    //     });
    //   }
    // },

    onSubmit: async (values) => {
      const body = visit?.anotherCarers?.length
        ? {
            anotherCarers: [
              ...visit.anotherCarers?.map((c) => ({
                carer: c.carer._id,
                type: c.type,
              })),
              {
                carer: values.staff.value,
                type: values.type,
              },
            ],
          }
        : {
            anotherCarers: [
              {
                carer: values.staff.value,
                type: values.type,
              },
            ],
          };

      try {
        await handleUpdateVisit(body);
        setShow(false);
        dispatch(setSelectedVisit(null));
      } catch (error) {
        errorToast("Failed to update visit", {
          autoClose: 3500,
        });
      }
    },
  });

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRemoveCarer = async () => {
    try {
      // loadingToast(`Removing ${terms?.staff}...`);
      const freshVisit = (visits || []).find(
        (v) => v?._id === selectedVisit?.extendedProps?._id
      ) as Visit;

      const newCarers =
        carerType === "carerAssigned"
          ? freshVisit?.carerAssigned
              ?.filter((c) => c._id !== selectedCarer)
              .map((c) => c._id)
          : freshVisit?.anotherCarers
              ?.filter((c) => c.carer._id !== selectedCarer)
              .map((c) => ({
                carer: c.carer._id,
                type: c.type,
              }));

      const body = {
        [carerType]: newCarers || [],
      };

      try {
        await handleUpdateVisit(body);
        updateSingleVisit({ _id: freshVisit._id, ...body });
        setRelatedResources((prev: any) =>
          prev?.filter((item: any) => item?._id !== selectedCarer)
        );
        setAnotherCarers((prev: any) =>
          prev?.filter((item: any) => item?.carer?._id !== selectedCarer)
        );
        setSelectedVisit({ ...selectedVisit, ...body });
      } catch (error) {
        logger.error("error: ", error);
      }

      const calendarApi = getCalendarApi();
      const e = calendarApi?.getEvents().find((ev) => {
        return (
          (ev.extendedProps._id === freshVisit._id ||
            ev.extendedProps?._id === freshVisit.origin?._id) &&
          dayjs(ev.start).isSame(dayjs(selectedVisit?.start), "day")
        );
      });

      if (e) {
        dispatch(setSelectedVisit(e));
      }
      // successToast(`${terms?.staff} removed successfully`);
      setAnchorEl(null);
    } catch (error: any) {
      const msg =
        error.response?.data?.message || `Failed to remove ${terms?.staff}`;
      errorToast(msg, {
        autoClose: 3500,
      });
    }
  };

  const fetchStaffData = async () => {
    try {
      if (loading || !hasMore) return;
      setLoading(true);
      const response = await getStaff({ page, limit: "n/a", includeMe: true });

      if (response.success) {
        setHasMore(response.data.length > 0);

        const data = response.data.map((el: any) => ({
          label: el.name,
          value: el._id,
        }));

        setOptions((prev) => [...prev, ...data]);
      }
    } catch (error) {
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (event: Event) => {
    const target = event.target as HTMLUListElement;
    const bottom =
      target.scrollHeight - target.scrollTop === target.clientHeight;

    if (bottom && !loading && hasMore) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  useEffect(() => {
    if (dropdownRef.current) {
      const dropdown = dropdownRef.current;
      dropdown.addEventListener("scroll", handleScroll);
      return () => dropdown.removeEventListener("scroll", handleScroll);
    }
  }, []);

  useEffect(() => {
    fetchStaffData();
  }, [page]);

  const handleUpdateVisit = async (
    newBody: Partial<Omit<Visit, "anotherCarers">> & {
      anotherCarers?: {
        carer: string;
        type: string;
      }[];
    }
  ) => {
    try {
      const date = (selectedVisit as EventApi).start || undefined;
      const newVisit = {
        origin: visit?.origin || visit._id,
        carerReq: visit.carerReq,
        rrule: {
          frequency: visit.rrule?.freq || "daily",
        },
        session: visit.session,
        startTime: date,
        endTime: dayjs(date).add(visit.duration.raw, "minute").toDate(),
        startDate: date,
        endDate: dayjs(date).add(visit.duration.raw, "minute").toDate(),
        serviceUser: visit.serviceUser._id,
        recurring: false,
        ...newBody,
      };

      const originalEvent = visit?.origin || visit;

      const oldVisit = {
        exDate: [
          ...(visit.exdate || []),
          (selectedVisit as EventApi).start as Date,
        ].filter((date, index, arr) => {
          const day = new Date(date).toDateString();

          return (
            index === arr.findIndex((d) => new Date(d).toDateString() === day)
          );
        }),
        recurring:
          newVisit.carerAssigned?.length === 0
            ? (originalEvent as Visit)?.recurring
            : visit.recurring,
        originalEventId: originalEvent?._id,
      };

      await updateVisit(visit._id, newVisit, oldVisit);
    } catch (error: any) {
      const msg =
        error.response?.data?.message || `Failed to remove ${terms?.staff}`;
      errorToast(msg, {
        autoClose: 3500,
      });
    }
  };

  return (
    <DrawerStyled>
      <Box className="Careteam">
        {show ? (
          <>
            <Box className="teamAdd">
              <h3>
                <SVG.RightArrow onClick={() => setShow(false)} width="20px" />
                &nbsp;Allocated another type of employees
              </h3>
              <Box>
                <FormGroup>
                  <AutoCompleteInput
                    multiple={false}
                    inputProps={{
                      label: terms?.staff,
                      placeholder: `Select ${terms?.staff || ""}`,
                      className: "personal",
                      errorText:
                        (touched.staff as any)?.value &&
                        (errors.staff as any)?.value
                          ? "Staff  is required"
                          : touched.staff && errors.staff
                            ? "Staff is required"
                            : undefined,
                    }}
                    onSelectItem={(event, newValue) => {
                      setFieldValue("staff", newValue);
                      setFieldTouched("staff", true);
                    }}
                    onBlur={() => setFieldTouched("staff", true)}
                    value={values.staff}
                    options={filteredOptions}
                  />
                </FormGroup>
                <FormGroup>
                  <SelectInput
                    options={roster}
                    label="Type"
                    placeholder="Select type"
                    className="personal"
                    {...getFieldProps("type")}
                    errorText={touched.type && errors.type}
                  />
                </FormGroup>
                <CostumeButton
                  className="invite"
                  loading={isSubmitting}
                  onClick={() => handleSubmit()}
                  sx={{
                    Width: "150px !important",
                    Height: "37px !important",
                    margin: "13px 10px !important",
                    Padding: "8px, 30px, 8px, 30px",
                  }}
                >
                  Save
                </CostumeButton>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Box>
              <Box className="main">
                <Typography variant="h4" className="assign">
                  Assigned
                </Typography>
                <Box className="staffTeam">
                  <Typography className="required">
                    <SVG.RequiredUsers width="12px" />
                    {selectedVisit?.extendedProps.carerReq} required
                  </Typography>
                  {![VisitStatus.COMPLETED].includes(visit?.status) && (
                    <CostumeButton
                      onClick={handleOpenCarerModal}
                      className="drawer-care-team-action"
                    >
                      Edit care team
                    </CostumeButton>
                  )}
                </Box>
              </Box>

              <ListView
                data={relatedResources}
                loading={staffLogLoading}
                emptyComponent={
                  <Box
                    sx={{
                      display: "grid",
                      placeItems: "center",
                      height: "10em",
                    }}
                  >
                    <Typography variant="h6" className="assign">
                      Unassigned
                    </Typography>
                  </Box>
                }
                renderItem={(resource, i) => {
                  const { landlineNumber, mobileNumber, phoneNumber } =
                    resource;

                  return (
                    <Box key={i} className="Detail">
                      <Box className="planDetail">
                        <Box>
                          <Box
                            className="care-team-summary"
                            sx={{ display: "flex", margin: "10px 3px" }}
                          >
                            <CustomAvatar
                              name={resource?.name}
                              height={50}
                              width={50}
                              src={resource?.avatar}
                              style={{
                                background: "#fff",
                                boxShadow: "0px 0px 7px #ccc",
                              }}
                            />
                            <Box
                              className="care-team-summary-copy"
                              sx={{ margin: "0px 8px" }}
                            >
                              <h6
                                className="care-team-summary-line"
                                style={{
                                  margin: "0 0 0 3px",
                                  color: " #000",
                                  fontSize: " 20px",
                                  fontWeight: "500",
                                  wordBreak: "break-word",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {resource?.name}
                              </h6>
                              <span
                                className="care-team-summary-line"
                                style={{
                                  margin: "0 0 0 3px",
                                  color: "var(--1-f-4970, #1F4970)",
                                  fontSize: " 14px",
                                  fontWeight: "500",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {dayjs(selectedVisit?.start)
                                  .utc()
                                  .format("HH:mm")}{" "}
                                -{" "}
                                {dayjs(selectedVisit?.end)
                                  .utc()
                                  .format("HH:mm")}
                                ,{" "}
                                {dayjs(selectedVisit?.start)
                                  .utc()
                                  .format("DD MMM YYYY")}
                              </span>
                            </Box>
                          </Box>
                          <span className="Timer">
                            <SVG.Calender
                              style={{ width: "20px", margin: "0px 10px" }}
                            />{" "}
                            {staffLogCount?.[resource._id] || "0"} visits in the
                            last 90 days
                          </span>
                          <span className="Timer">
                            <SVG.Phone
                              style={{ width: "20px", margin: "0px 10px" }}
                            />
                            {[landlineNumber, mobileNumber, phoneNumber]
                              .filter(Boolean)
                              .join(", ") || "No phone number provided"}
                          </span>
                        </Box>
                        {visit?.status !== VisitStatus.COMPLETED && (
                          <Box
                            sx={{
                              margin: "15px 10px",
                              "& svg": {
                                cursor: "pointer",
                              },
                            }}
                          >
                            <SVG.DotsHorizontal
                              width="20px"
                              onClick={(e: React.MouseEvent<HTMLElement>) => {
                                setCarerType("carerAssigned");
                                setSelectedCarer(resource._id);
                                logger.debug(
                                  "Selected Carer ID:",
                                  resource._id
                                );
                                setAnchorEl(e.currentTarget);
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                }}
              />

              {!!anotherCarers && (
                <>
                  <Box className="main">
                    <Typography variant="h4" className="assign">
                      Other Assigned Employees
                    </Typography>
                  </Box>
                  <ListView
                    data={anotherCarers || []}
                    renderItem={(staff, i) => {
                      const { landlineNumber, mobileNumber, phoneNumber } =
                        staff?.carer;
                      return (
                        <Box key={i} className="Detail">
                          <Box className="planDetail">
                            <Box>
                              <Box
                                className="care-team-summary"
                                sx={{ display: "flex", margin: "15px 10px" }}
                              >
                                <CustomAvatar
                                  name={staff.carer?.name}
                                  height={50}
                                  width={50}
                                  src={staff.carer?.avatar}
                                  style={{
                                    background: "#ccc",
                                    border: "1px solid #ccc",
                                  }}
                                />
                                <Box
                                  className="care-team-summary-copy"
                                  sx={{ margin: "0px 8px" }}
                                >
                                  <h6
                                    className="care-team-summary-line"
                                    style={{
                                      margin: "0 0 0 3px",
                                      color: " #000",
                                      fontSize: " 20px",
                                      fontWeight: "500",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {staff.carer?.name}
                                  </h6>
                                  <span
                                    className="care-team-summary-line"
                                    style={{
                                      margin: "0 0 0 3px",
                                      color: "var(--1-f-4970, #1F4970)",
                                      fontSize: " 14px",
                                      fontWeight: "500",
                                      textTransform: "capitalize",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {staff.type}
                                  </span>
                                </Box>
                              </Box>
                              <span className="timer">
                                <SVG.Calender
                                  style={{ width: "20px", margin: "0px 10px" }}
                                />{" "}
                                {staffLogCount?.[staff.carer._id] || "0"} visits
                                in the last 90 days
                              </span>
                              <span className="timer">
                                <SVG.Phone
                                  style={{ width: "20px", margin: "0px 10px" }}
                                />
                                {[landlineNumber, mobileNumber, phoneNumber]
                                  .filter(Boolean)
                                  .join(", ") || "No phone number provided"}
                              </span>
                            </Box>
                            <Box
                              sx={{
                                margin: "15px 10px",
                                "& svg": {
                                  cursor: "pointer",
                                },
                              }}
                            >
                              <SVG.DotsHorizontal
                                width="20px"
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                  setCarerType("anotherCarers");
                                  setSelectedCarer(staff.carer._id);
                                  setAnchorEl(e.currentTarget);
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      );
                    }}
                  />
                </>
              )}
            </Box>
          </>
        )}
        <Box>
          {show
            ? ""
            : ![VisitStatus.COMPLETED].includes(visit?.status) && (
                <CostumeButton
                  className="addMember"
                  onClick={() => setShow(!show)}
                >
                  <SVG.Plus />{" "}
                  <span className="service-memeberlist-add-new">
                    {" "}
                    assign another type of employee
                  </span>
                </CostumeButton>
              )}
        </Box>
      </Box>

      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <MenuItem onClick={handleRemoveCarer}>Remove from visit</MenuItem>
      </Menu>
    </DrawerStyled>
  );
}

export default CareTeam;
